import { useEffect, useRef, useCallback } from 'react';

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Traps focus inside a container and closes on Escape.
 * @param {React.RefObject<HTMLElement>} containerRef - Ref to the modal content element
 * @param {boolean} isActive - Whether the trap is active (modal open)
 * @param {() => void} onClose - Called when Escape is pressed
 * @param {boolean} [focusFirstOnOpen=true] - Whether to focus the first focusable element when opened
 */
export function useFocusTrap(containerRef, isActive, onClose, focusFirstOnOpen = true) {
  const previousActive = useRef(null);

  const getFocusables = useCallback(() => {
    if (!containerRef.current) return [];
    return [...containerRef.current.querySelectorAll(FOCUSABLE)].filter(
      (el) => el.tabIndex !== -1 && !el.disabled
    );
  }, [containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!isActive || !container) return;

    previousActive.current = document.activeElement;
    const focusables = getFocusables();

    if (focusFirstOnOpen && focusables.length > 0) {
      focusables[0].focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusables = getFocusables();
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    container.addEventListener('keydown', handleKeyDown);
    return () => {
      container?.removeEventListener('keydown', handleKeyDown);
      if (previousActive.current && typeof previousActive.current.focus === 'function') {
        previousActive.current.focus();
      }
    };
  }, [isActive, onClose, getFocusables, focusFirstOnOpen, containerRef]);
}
